import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { InfoList } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetCustomer } from '@/generated/customer';
import { useGetTranslations } from '@/generated/translations';
import styles from './AccountView.module.scss';

interface AccountViewProps {
	accountId: number;
	wrapperClassName?: string;
}

export const AccountView: React.FunctionComponent<AccountViewProps> = ({ accountId, wrapperClassName }) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	const { data: response, isLoading } = useGetCustomer(segmentationId, accountId);
	const { leftColumnData, rightColumnData } = useMemo(() => {
		if (!response) {
			return { leftColumnData: null, rightColumnData: null };
		}

		const {
			customerNumber = '',
			segment = '',
			subSegment = '',
			group = '',
			type = '',
			status = '',
			internalName = '',
			groupCoName = '',
			name = '',
			nameLine2 = '',
			url,
		} = response.data;

		const nameValue = url ? (
			<a href={url} target="_blank" rel="noopener noreferrer nofollow">
				{name}
			</a>
		) : (
			name
		);

		return {
			leftColumnData: {
				accountNo: customerNumber,
				name: nameValue,
				nameLine2,
				internalName,
				segment,
				subSegment,
			} as Record<string, string | number | null | JSX.Element>,
			rightColumnData: {
				groupCoName,
				group,
				type,
				status,
			} as Record<string, string | number | null | JSX.Element>,
		};
	}, [response]);

	return (
		<div className={styles.wrapper}>
			{!isLoading && leftColumnData && (
				<div className={classNames(styles.columns, 'u-mt-md')}>
					<InfoList
						data={leftColumnData}
						translations={translations?.data.customer}
						dlClassName={styles.accountInfo}
						groupClassName={styles.accountInfoGroup}
						wrapperClassName={wrapperClassName}
						numberOfColumns={1}
						listOnly
					/>
					<InfoList
						data={rightColumnData ?? {}}
						translations={translations?.data.customer}
						dlClassName={styles.accountInfo}
						groupClassName={styles.accountInfoGroup}
						wrapperClassName={wrapperClassName}
						numberOfColumns={1}
						listOnly
					/>
				</div>
			)}
			<div className={styles.management}>
				<div>
					<p className={styles.blue}>{response?.data?.owner ?? '--'}</p>
					<p>{translations?.data.customer.owner}</p>
				</div>
				<div>
					<p>{response?.data?.keyAccountManager ?? '--'}</p>
					<p>{translations?.data.customer.keyAccountManager}</p>
				</div>
				<div>
					<>{response?.data?.thirdPartyResponsible ?? '--'}</>
					<p>{translations?.data.customer.thirdPartyResponsible}</p>
				</div>
			</div>
		</div>
	);
};
